import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { addDays } from 'date-fns'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { useUserContext } from 'contexts/user-context'

import { useGetCustomerEncoders, useGetCustomerMatches, useGetCustomerStreams } from 'api'
import { MAX_WEEKS_DISPLAY_IN_DAYS, STARTING_DATE } from 'settings'

import type { Match, Stream } from 'types'

const DEFAULT_QUESTION = 'What do you want to chat about?'

type GetUserResponse = {
  success: boolean
  status: number
  data?: {
    id: number // FreshChat's internal ID for the user
    identifier: string // This is our own cognitoId for the user
    firstName: string
    lastName: string
    name: string
    email: string
    nameOrEmailForUser: string
    restoreId: string // This is what's used to restore the conversation history when the user logs in on a different device
  }
}

type ConversationProperties = {
  cf_club?: string
  cf_club_id?: string
  cf_raised_issue?: string
  cf_kit_number?: string
  cf_stream_id?: string
  cf_match_id?: string
}

type Primitive = string | number | boolean | undefined
declare global {
  interface Window {
    fcWidget: {
      conversation: {
        setConversationProperties: (properties: ConversationProperties) => void
      }
      setExternalId: (id?: string) => void
      user: {
        clear: () => void
        get: (callback?: (user: GetUserResponse) => void) => Promise<GetUserResponse>
        setFirstName: (firstName?: string) => void
        setLastName: (lastName?: string) => void
        setEmail: (email?: string) => void
        setProperties: (properties: { [key: string]: string }) => void
      }
      on: (event: string, callback: (res?: unknown) => void) => void
      setConfig: (config: { [key: string]: Primitive }) => void
    }
    fcSettings: {
      onInit: () => void
    }
    fwSettings: {
      [key: string]: Primitive
    }
    fcWidgetMessengerConfig: {
      [key: string]: Primitive | { [key: string]: Primitive }
    }
    fwcrm: {
      on: (event: string, callback: (res?: unknown) => void) => void
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FreshworksWidget: (...args: any[]) => void | { q: IArguments[] }
  }
}

const sortGroups = (a: string, b: string) => {
  const [gradeA, roundA] = a.split('-')
  const [gradeB, roundB] = b.split('-')

  if (gradeA === gradeB) {
    // Extract the number at the end of the round, if it exists
    const numA = parseInt(roundA.match(/\d+$/)?.[0] || '0')
    const numB = parseInt(roundB.match(/\d+$/)?.[0] || '0')

    return numA - numB || roundA.localeCompare(roundB)
  } else {
    return gradeA.localeCompare(gradeB)
  }
}

const useLoadScript = () => {
  const { user } = useUserContext()

  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let fcScript: HTMLScriptElement
    let fwScript: HTMLScriptElement
    let timeout: NodeJS.Timeout
    const onScriptLoad = () => {
      timeout = setTimeout(() => {
        setIsLoaded(true)
      }, 500) // Need slight delay to ensure the widget is fully loaded
    }
    const onScriptError = (src: string) => {
      setError(new Error(`Failed to load script: ${src}`))
    }
    if (user && import.meta.env.VITE_FRESHCHAT_SRC && import.meta.env.VITE_FRESHDESK_SRC) {
      window.fcSettings = {
        onInit: () => {
          // Add any additional init steps here if needed
          window.fcWidget?.on('widget:loaded', () => {
            window.fcWidget.setExternalId(user.email)
            window.fcWidget.user.setFirstName(user.given_name)
            window.fcWidget.user.setLastName(user.family_name)
            window.fcWidget.user.setEmail(user.email)
          })
        },
      }
      window.fwSettings = {
        widget_id: 51000000195,
      }

      // Define the FreshworksWidget function if it doesn't already exist
      if (typeof window.FreshworksWidget !== 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const n = function (...args: any[]) {
          ;(n.q = n.q || []).push(args)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        n.q = [] as any[]
        window.FreshworksWidget = n
      }

      fcScript = document.createElement('script')
      fcScript.src = import.meta.env.VITE_FRESHCHAT_SRC
      fcScript.async = true
      fcScript.type = 'text/javascript'
      fcScript.setAttribute('chat', 'true')

      fcScript.addEventListener('load', onScriptLoad)
      fcScript.addEventListener('error', () => onScriptError(import.meta.env.VITE_FRESHCHAT_SRC))
      document.head.appendChild(fcScript)

      fwScript = document.createElement('script')
      fwScript.src = import.meta.env.VITE_FRESHDESK_SRC
      fwScript.async = true
      fwScript.defer = true
      fwScript.type = 'text/javascript'

      fwScript.addEventListener('load', onScriptLoad)
      fwScript.addEventListener('error', () => onScriptError(import.meta.env.VITE_FRESHDESK_SRC))
      document.head.appendChild(fwScript)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      if (fcScript) {
        fcScript.removeEventListener('load', onScriptLoad)
        fcScript.removeEventListener('error', () => onScriptError(import.meta.env.VITE_FRESHCHAT_SRC))
        document.head.removeChild(fcScript)
      }
      if (fwScript) {
        fwScript.removeEventListener('load', onScriptLoad)
        fwScript.removeEventListener('error', () => onScriptError(import.meta.env.VITE_FRESHDESK_SRC))
        document.head.removeChild(fwScript)
      }
    }
  }, [user])

  return { isLoaded, error }
}

const PrefillQuestions = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery('(max-width: 600px) or (pointer: coarse)')[0]
  const { isLoaded } = useLoadScript()
  const { selectedCustomer } = useUserContext()

  const [conversationProperties, setConversationProperties] = useState<ConversationProperties>({})
  const [selectedDevice, setSelectedDevice] = useState('')
  const [selectedStream, setSelectedStream] = useState('')
  const [selectedMatch, setSelectedMatch] = useState('')
  const { isOpen, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    if (isLoaded) {
      window.fcWidget?.setConfig({ fullscreen: isMobile })
    }
  }, [isLoaded, isMobile])

  useEffect(() => {
    window.fcWidget?.conversation?.setConversationProperties(conversationProperties)
  }, [conversationProperties])

  useEffect(() => {
    if (selectedCustomer?.customerId) {
      // Update conversation properties with the selected customer
      setConversationProperties(prev => ({
        ...prev,
        cf_club: selectedCustomer.customerName,
        cf_club_id: `${selectedCustomer.customerId}`,
      }))
    }
  }, [selectedCustomer?.customerId, selectedCustomer?.customerName])

  const queryPayload = {
    customerId: selectedCustomer?.customerGuid ?? '',
    startDate: STARTING_DATE.toISOString(),
    endDate: addDays(STARTING_DATE, MAX_WEEKS_DISPLAY_IN_DAYS).toISOString(),
    includeParticipatingMatches: true,
    alt: 'schedule',
  }

  const {
    data: encoders = [],
    refetch: fetchEncoders,
    isFetching: encodersIsFetching,
    isRefetching: encodersIsRefetching,
  } = useGetCustomerEncoders(
    {
      customerGuid: selectedCustomer?.customerGuid,
    },
    { enabled: false }
  )
  const isFetchingEncoders = encodersIsFetching || encodersIsRefetching

  const {
    data: streams = [],
    refetch: fetchStreams,
    isFetching: streamsIsFetching,
    isRefetching: streamsIsRefetching,
  } = useGetCustomerStreams(queryPayload, { enabled: false })
  const isFetchingStreams = streamsIsFetching || streamsIsRefetching

  const streamsByGroup = useMemo(
    () =>
      streams.reduce(
        (acc, cur) => {
          const groupKey = `${cur.streamConfig?.compMatch?.gradeName} - ${cur.streamConfig?.compMatch?.round}`
          if (!acc[groupKey]) acc[groupKey] = []
          acc[groupKey].push(cur)
          return acc
        },
        {} as Record<string, Stream[]>
      ),
    [streams]
  )

  const sortedStreamGroups = Object.keys(streamsByGroup).sort(sortGroups)

  const {
    data: matches = [],
    refetch: fetchMatches,
    isFetching: matchesIsFetching,
    isRefetching: matchesIsRefetching,
  } = useGetCustomerMatches(queryPayload, { enabled: false, staleTime: Infinity })
  const isFetchingMatches = matchesIsFetching || matchesIsRefetching

  const matchesByGroup = useMemo(
    () =>
      matches.reduce(
        (acc, cur) => {
          const groupKey = `${cur.gradeName} - ${cur.round}`
          if (!acc[groupKey]) acc[groupKey] = []
          acc[groupKey].push(cur)
          return acc
        },
        {} as Record<string, Match[]>
      ),
    [matches]
  )

  const sortedMatchGroups = Object.keys(matchesByGroup).sort(sortGroups)

  const onDeviceSelect = (device: string) => {
    setSelectedDevice(device)
    showAcknowledgement()
  }

  const onStreamSelect = (streamId: string) => {
    setSelectedStream(streamId)
    showAcknowledgement()
  }

  const onMatchSelect = (matchId: string) => {
    setSelectedMatch(matchId)
    showAcknowledgement()
  }

  useEffect(() => {
    if (selectedDevice) {
      setConversationProperties(prev => ({
        ...prev,
        cf_match_id: '',
        cf_stream_id: '',
        cf_kit_number: selectedDevice,
        cf_raised_issue: 'Issue with Kit',
      }))
    }
  }, [selectedDevice])

  useEffect(() => {
    if (selectedMatch) {
      setConversationProperties(prev => ({
        ...prev,
        cf_kit_number: '',
        cf_stream_id: '',
        cf_match_id: selectedMatch,
        cf_raised_issue: 'Issue with scheduling new stream',
      }))
    }
  }, [selectedMatch])

  useEffect(() => {
    if (selectedStream) {
      setConversationProperties(prev => ({
        ...prev,
        cf_kit_number: '',
        cf_match_id: '',
        cf_stream_id: selectedStream,
        cf_raised_issue: 'Issue with existing stream',
      }))
    }
  }, [selectedStream])

  useEffect(() => {
    if (isLoaded) {
      // Prompt the user to select what their query is about
      if (window.fcWidget) {
        window.fcWidget.on('widget:opened', () => {
          fetchEncoders()
          onOpen()
        })
      } else {
        console.warn('Failed to load FreshChat SDK')
      }
    }
  }, [isLoaded, onOpen, fetchEncoders])

  const [step, setStep] = useState(1)
  const [prefillQuestion, setPrefillQuestion] = useState(DEFAULT_QUESTION)
  const [firstAnswer, setFirstAnswer] = useState<'stream' | 'kit' | 'general'>()
  const [secondAnswer, setSecondAnswer] = useState<'new' | 'existing' | 'something_else'>()

  const resetQuestions = () => {
    setStep(1)
    setPrefillQuestion(DEFAULT_QUESTION)
    setFirstAnswer(undefined)
    onClose()
  }

  const answerFirstQuestion = (answer: 'stream' | 'kit' | 'general') => {
    switch (answer) {
      case 'stream':
        setPrefillQuestion('What do you need help with?')
        fetchMatches()
        fetchStreams()
        break
      case 'kit':
        setPrefillQuestion('Which kit is this regarding?')
        break
      case 'general':
        setConversationProperties(prev => ({
          ...prev,
          cf_kit_number: '',
          cf_match_id: '',
          cf_stream_id: '',
          cf_raised_issue: 'General Query',
        }))
        resetQuestions()
        break
    }
    setFirstAnswer(answer)
    setStep(2)
  }

  const answerSecondQuestion = (answer: 'new' | 'existing' | 'something_else') => {
    switch (answer) {
      case 'new':
        setPrefillQuestion('For which match are you scheduling a stream?')
        break
      case 'existing':
        setPrefillQuestion('With which stream are you having an issue?')
        break
      case 'something_else':
        setConversationProperties(prev => ({
          ...prev,
          cf_kit_number: '',
          cf_match_id: '',
          cf_stream_id: '',
          cf_raised_issue: 'Something else stream related',
        }))
        resetQuestions()
        break
    }
    setSecondAnswer(answer)
    setStep(3)
  }

  const showAcknowledgement = () => {
    setStep(4)
    const interval = setInterval(() => {
      resetQuestions()
      clearInterval(interval)
    }, 2000)
  }

  return (
    <Modal isOpen={isOpen} onClose={resetQuestions} size={isMobile ? 'full' : 'md'}>
      <ModalOverlay />
      <ModalContent
        {...(!isMobile && {
          position: 'fixed',
          right: '35px',
          bottom: '35px',
          my: 0,
          w: '384px',
          h: '720px',
        })}
      >
        <ModalHeader color="#FFF" bgColor="#38c773" {...(!isMobile && { borderTopRadius: '0.375rem' })}>
          <Text w="80%">{prefillQuestion}</Text>
        </ModalHeader>
        <ModalCloseButton w={8} h={8} top="1rem" right={3} opacity={0.6} bgColor="rgba(255,255,255,0.2)" color="#FFF" />
        <ModalBody my={4}>
          <Stack h="100%" direction="column" justifyContent="flex-start">
            {/* Is the chat about Stream, Device, or General Query */}
            {step === 1 && (
              <>
                <Button onClick={() => answerFirstQuestion('stream')}>Stream</Button>
                <Tooltip isDisabled={isFetchingEncoders || !!encoders.length} label="There are no kits available">
                  <Button
                    isLoading={isFetchingEncoders}
                    loadingText="Loading kits..."
                    isDisabled={!(isFetchingEncoders || !!encoders.length)}
                    onClick={() => answerFirstQuestion('kit')}
                  >
                    Kit
                  </Button>
                </Tooltip>
                <Button onClick={() => answerFirstQuestion('general')}>General Query</Button>
              </>
            )}

            {/* Is the chat about scheduling a new stream, managing an existing one, or something else */}
            {step === 2 && firstAnswer === 'stream' && (
              <>
                <Tooltip isDisabled={isFetchingMatches || !!matches.length} label="There is nothing to schedule">
                  <Button
                    isLoading={isFetchingMatches}
                    loadingText="Loading matches..."
                    isDisabled={!(isFetchingMatches || matches.length)}
                    onClick={() => answerSecondQuestion('new')}
                  >
                    Scheduling a new Stream
                  </Button>
                </Tooltip>
                <Tooltip isDisabled={isFetchingStreams || !!streams.length} label="There are no existing streams">
                  <Button
                    isLoading={isFetchingStreams}
                    loadingText="Loading streams..."
                    isDisabled={!(isFetchingStreams || streams.length)}
                    onClick={() => answerSecondQuestion('existing')}
                  >
                    Issue with an existing stream
                  </Button>
                </Tooltip>
                <Button onClick={() => answerSecondQuestion('something_else')}>Something else</Button>
              </>
            )}

            {/* Which device is this regarding? */}
            {step === 2 &&
              firstAnswer === 'kit' &&
              (encoders.length > 5 ? (
                <Select
                  placeholder="Select a device"
                  onChange={e => onDeviceSelect(e.target.value)}
                  value={selectedDevice}
                >
                  {encoders.map(encoder => (
                    <option key={encoder.deviceCode} value={encoder.friendlyName}>
                      {encoder.friendlyName} [{encoder.deviceKey}]
                    </option>
                  ))}
                </Select>
              ) : (
                <>
                  {encoders.map(encoder => (
                    <Button key={encoder.deviceCode} onClick={() => onDeviceSelect(encoder.friendlyName)}>
                      {encoder.friendlyName} [{encoder.deviceKey}]
                    </Button>
                  ))}
                </>
              ))}

            {/* Which match are you trying to schedule a stream for? */}
            {step === 3 &&
              secondAnswer === 'new' &&
              (matches.length > 5 ? (
                <Select
                  placeholder="Select a match"
                  onChange={e => onMatchSelect(e.target.value)}
                  value={selectedMatch}
                >
                  {sortedMatchGroups.map(group => (
                    <optgroup key={group} label={group}>
                      {matchesByGroup[group].map(match => (
                        <option key={match.id} value={match.id}>
                          {match.title.trim()}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              ) : (
                sortedMatchGroups.map(group => (
                  <Fragment key={group}>
                    <Text textStyle="label">{group}</Text>
                    {matchesByGroup[group].map(match => (
                      <Button key={match.id} onClick={() => onMatchSelect(match.id)}>
                        {match.title.trim()}
                      </Button>
                    ))}
                  </Fragment>
                ))
              ))}

            {/* Which stream are you having an issue with? */}
            {step === 3 &&
              secondAnswer === 'existing' &&
              (streams.length > 5 ? (
                <Select
                  placeholder="Select a stream"
                  onChange={e => onStreamSelect(e.target.value)}
                  value={selectedStream}
                >
                  {sortedStreamGroups.map(group => (
                    <optgroup key={group} label={group}>
                      {streamsByGroup[group].map(stream => stream.streamGuid!)}
                    </optgroup>
                  ))}
                </Select>
              ) : (
                <>
                  {sortedStreamGroups.map(group => (
                    <Fragment key={group}>
                      <Text textStyle="label">{group}</Text>
                      {streamsByGroup[group].map(stream => (
                        <Button key={stream.streamGuid} onClick={() => onStreamSelect(stream.streamGuid!)}>
                          {stream.title.trim()}
                        </Button>
                      ))}
                    </Fragment>
                  ))}
                </>
              ))}

            {/* Acknowledgement */}
            {step === 4 && (
              <Stack spacing={2} justifyContent="center" alignItems="center" h="100%">
                <FontAwesomeIcon
                  icon={['fas', 'circle-check']}
                  size={isMobile ? '3x' : '5x'}
                  color={theme.baseColors.success.main}
                />
                <Text textStyle="H5">Thank you!</Text>
              </Stack>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const FreshChat = () => {
  const { isLoaded, error } = useLoadScript()

  if (error) {
    console.error('Failed to load FreshChat SDK:', error)
    return null
  }

  return isLoaded ? <PrefillQuestions /> : null
}

export default FreshChat
